import {
  ArrowForward,
  CheckCircleOutline,
  CreditCardOutlined,
  EmailOutlined,
  MarkEmailReadOutlined,
  PersonOutline,
  PhoneOutlined,
  StorefrontOutlined,
  TimerOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  Link,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import { OnboardingServiceSignupAuthServiceEnum } from "@incendium/api";
import {
  isValidStoreUrl,
  normalizeStoreUrl,
} from "features/dataConnectors/utils/normalizeStoreUrl";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import useOAuthPopup from "Hooks/useOAuthPopup";
import SocialSignupButton from "features/signup/woocommerce/SocialSignupButton";
import {
  loginWithSignupAuthResponse,
  wooCommerceSignup,
  wooCommerceSocialSignup,
  wooCommerceSocialSignupAuthUrl,
} from "features/signup/services/signupService";
import { useNotification } from "Hooks";
import { motion } from "motion/react";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useHistory } from "react-router-dom";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { isValidEmail, isValidPersonName } from "services/validators";

const fieldMotion = (delay: number) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
});

const fieldIconAdornment = (icon: ReactNode) => ({
  startAdornment: <InputAdornment position="start">{icon}</InputAdornment>,
});

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  padding: theme.spacing(1.5, 0.5),
  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
  },
}));

type SignupMethod = "email";
const CONTINUE_BUTTON_CLICKED_STAGE_KEY = "AaXD8cw";
const SIGNUP_STAGE_KEY = "AaXD8c8";

function WooCommerceSignupForm() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [storeUrl, setStoreUrl] = useState("");
  const [storeUrlError, setStoreUrlError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [socialSubmitting, setSocialSubmitting] =
    useState<OnboardingServiceSignupAuthServiceEnum | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [signupMethod, setSignupMethod] = useState<SignupMethod | null>(null);
  const pendingSignupRef = useRef<{
    service: OnboardingServiceSignupAuthServiceEnum;
    storeUrl: string;
  } | null>(null);
  const { showErrorNotification } = useNotification();
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [continueButtonClicked, setContinueButtonClicked] = useState(false);
  const [continueButtonClickedRegistered, setContinueButtonClickedRegistered] =
    useState(false);

  const handleOAuthCode = useCallback(
    async (code: string) => {
      const pending = pendingSignupRef.current;
      if (!pending) {
        return;
      }

      try {
        const response = await wooCommerceSocialSignup({
          service: pending.service,
          code,
          storeUrl: pending.storeUrl,
          redirectUrl: OAUTH_REDIRECT_URL,
        });
        (window as any).inc("append_lead_status", SIGNUP_STAGE_KEY);
        (window as any).inc("add_conversion", "signup");
        (window as any).inc("go");
        await loginWithSignupAuthResponse(response, dispatch);

        history.push("/");
      } catch (error: any) {
        let errorMessage = "Failed to create account. Please try again.";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // keep default message
        }
        showErrorNotification(errorMessage);
      } finally {
        pendingSignupRef.current = null;
        setSocialSubmitting(null);
      }
    },
    [dispatch, history, showErrorNotification],
  );

  const { startOAuth } = useOAuthPopup({
    onCode: handleOAuthCode,
    onDenied: () => {
      pendingSignupRef.current = null;
      setSocialSubmitting(null);
    },
    cancelledMessage: "Signup was cancelled",
  });

  const handleStoreUrlBlur = useCallback(() => {
    setStoreUrl((current) => normalizeStoreUrl(current));
    setStoreUrlError(null);
  }, []);

  const validateStoreUrlField = useCallback((): string | null => {
    const trimmed = storeUrl.trim();
    if (!trimmed) {
      return "Store URL is required";
    }
    if (!isValidStoreUrl(trimmed)) {
      return "Please enter a valid store URL";
    }
    return null;
  }, [storeUrl]);

  const handleContinueButtonClick = useCallback(() => {
    if (!isValidStoreUrl(storeUrl)) {
      setStoreUrlError("Please enter a valid store URL");
      return;
    }
    setStoreUrlError(null);
    setContinueButtonClicked(true);
  }, [storeUrl, setStoreUrlError]);

  const handleSubmit = useCallback(async () => {
    const normalizedStoreUrl = normalizeStoreUrl(storeUrl);
    setStoreUrl(normalizedStoreUrl);
    setSubmitting(true);
    try {
      await wooCommerceSignup({
        email,
        name,
        storeUrl: normalizedStoreUrl,
      });
      setSignupMethod("email");
      setSubmitted(true);
      (window as any).inc("append_lead_status", SIGNUP_STAGE_KEY);
      (window as any).inc("add_conversion", "signup");
      (window as any).inc("go");
    } catch (error: any) {
      let errorMessage = "Failed to create account. Please try again.";
      try {
        const errorData = await error.json();
        errorMessage = errorData.message || errorMessage;
      } catch {
        // keep default message
      }
      showErrorNotification(errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [email, name, storeUrl, showErrorNotification]);

  const handleSocialSignup = useCallback(
    async (service: OnboardingServiceSignupAuthServiceEnum) => {
      const validationError = validateStoreUrlField();
      if (validationError) {
        setStoreUrlError(validationError);
        return;
      }

      const normalizedStoreUrl = normalizeStoreUrl(storeUrl);
      setStoreUrl(normalizedStoreUrl);
      setStoreUrlError(null);
      setSocialSubmitting(service);
      pendingSignupRef.current = { service, storeUrl: normalizedStoreUrl };

      try {
        const opened = await startOAuth(async () => {
          const res = await wooCommerceSocialSignupAuthUrl({
            service,
            redirectUrl: OAUTH_REDIRECT_URL,
          });

          if (!res.authUrl) {
            throw new Error("Missing auth URL");
          }

          return res.authUrl;
        }, "signup-auth");

        if (!opened) {
          pendingSignupRef.current = null;
          setSocialSubmitting(null);
        }
      } catch (error: any) {
        pendingSignupRef.current = null;
        setSocialSubmitting(null);

        let errorMessage = "Failed to start signup. Please try again.";
        try {
          const errorData = await error.json();
          errorMessage = errorData.message || errorMessage;
        } catch {
          // keep default message
        }
        showErrorNotification(errorMessage);
      }
    },
    [storeUrl, validateStoreUrlField, startOAuth, showErrorNotification],
  );

  useEffect(() => {
    if (continueButtonClicked && !continueButtonClickedRegistered) {
      (window as any).inc(
        "append_lead_status",
        CONTINUE_BUTTON_CLICKED_STAGE_KEY,
        true,
      );
      setContinueButtonClickedRegistered(true);
    }
  }, [continueButtonClicked, continueButtonClickedRegistered]);

  const socialDisabled = submitting || socialSubmitting !== null;
  const hasValidStoreUrl = isValidStoreUrl(storeUrl);
  const isEmailSignupReady = useMemo(
    () => hasValidStoreUrl && isValidEmail(email) && isValidPersonName(name),
    [hasValidStoreUrl, email, name],
  );

  if (submitted && signupMethod === "email") {
    return (
      <Stack spacing={3} textAlign="center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Box
            sx={(theme) => ({
              display: "inline-flex",
              p: 2,
              borderRadius: "50%",
              backgroundColor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.main,
              mb: 1,
            })}
          >
            <CheckCircleOutline sx={{ fontSize: 48 }} />
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            You&apos;re all set, {name.split(" ")[0] || "there"}!
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.6}>
            Thanks for signing up. A verification email is on its way to{" "}
            <Typography
              component="span"
              variant="body1"
              fontWeight={600}
              color="text.primary"
            >
              {email}
            </Typography>
            .
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            textAlign="left"
            sx={(theme) => ({
              px: 2,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            })}
          >
            <MarkEmailReadOutlined
              sx={{ fontSize: 20, color: "primary.main", mt: 0.25 }}
            />
            <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
              It should arrive shortly. Open the link inside to verify your
              address and set your password, then you can connect your store and
              get started.
            </Typography>
          </Stack>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Typography variant="body2" color="text.secondary">
            Already verified?{" "}
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              fontWeight={600}
            >
              Log in
            </Link>
          </Typography>
        </motion.div>
      </Stack>
    );
  }

  return (
    <>
      <ValidatorForm onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Box>
            <Typography align="center" variant="h1" fontWeight={700}>
              Start free with Incendium
            </Typography>
            <Typography
              align="center"
              variant="body1"
              color="text.secondary"
              mt={0.75}
            >
              Connect your WooCommerce store and bring accuracy back with
              first-party analytics in just a couple of minutes.
            </Typography>
          </Box>
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              flexWrap="wrap"
              spacing={1.5}
              gap={1}
              mb={1}
            >
              <StyledChip
                label="2-min setup"
                size="small"
                icon={<TimerOutlined />}
              />
              <StyledChip
                label="No credit card"
                size="small"
                icon={<CreditCardOutlined />}
              />
              <StyledChip
                label="Built for WooCommerce"
                size="small"
                icon={<StorefrontOutlined />}
              />
            </Stack>
          </Box>

          <motion.div {...fieldMotion(0.2)}>
            <TextValidator
              fullWidth
              name="storeUrl"
              size="medium"
              value={storeUrl}
              variant="outlined"
              placeholder="Enter your store URL"
              validators={["required", "isStoreUrl"]}
              errorMessages={[
                "Store URL is required",
                "Please enter a valid store URL",
              ]}
              error={!!storeUrlError}
              helperText={storeUrlError ?? undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setStoreUrl(e.target.value);
                if (storeUrlError) {
                  setStoreUrlError(null);
                }
              }}
              onBlur={handleStoreUrlBlur}
              InputProps={fieldIconAdornment(
                <StorefrontOutlined fontSize="small" color="primary" />,
              )}
            />
          </motion.div>
          {(!continueButtonClicked || !hasValidStoreUrl) && (
            <motion.div {...fieldMotion(0.4)}>
              <Button
                fullWidth
                size="large"
                color="primary"
                onClick={handleContinueButtonClick}
              >
                Continue
              </Button>
            </motion.div>
          )}

          {hasValidStoreUrl && continueButtonClicked && (
            <>
              {!showEmailSignup && (
                <motion.div {...fieldMotion(0.27)}>
                  <Stack spacing={1.5}>
                    <SocialSignupButton
                      service={OnboardingServiceSignupAuthServiceEnum.GOOGLE}
                      label="Continue with Google"
                      loading={
                        socialSubmitting ===
                        OnboardingServiceSignupAuthServiceEnum.GOOGLE
                      }
                      disabled={socialDisabled || !hasValidStoreUrl}
                      onClick={() =>
                        handleSocialSignup(
                          OnboardingServiceSignupAuthServiceEnum.GOOGLE,
                        )
                      }
                    />
                    <SocialSignupButton
                      service={OnboardingServiceSignupAuthServiceEnum.FACEBOOK}
                      label="Continue with Facebook"
                      loading={
                        socialSubmitting ===
                        OnboardingServiceSignupAuthServiceEnum.FACEBOOK
                      }
                      disabled={socialDisabled || !hasValidStoreUrl}
                      onClick={() =>
                        handleSocialSignup(
                          OnboardingServiceSignupAuthServiceEnum.FACEBOOK,
                        )
                      }
                    />
                  </Stack>
                </motion.div>
              )}
              {showEmailSignup && (
                <>
                  <Stack spacing={3}>
                    <motion.div {...fieldMotion(0.41)}>
                      <TextValidator
                        fullWidth
                        name="email"
                        label="Email"
                        type="email"
                        size="medium"
                        value={email}
                        variant="outlined"
                        disabled={socialDisabled}
                        validators={["required", "isEmail"]}
                        errorMessages={[
                          "Email is required",
                          "Please enter a valid email",
                        ]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEmail(e.target.value.trim())
                        }
                        InputProps={fieldIconAdornment(
                          <EmailOutlined fontSize="small" color="primary" />,
                        )}
                      />
                    </motion.div>

                    <motion.div {...fieldMotion(0.48)}>
                      <TextValidator
                        fullWidth
                        name="name"
                        label="Name"
                        size="medium"
                        value={name}
                        variant="outlined"
                        disabled={socialDisabled}
                        validators={["required", "isPersonName"]}
                        errorMessages={[
                          "Name is required",
                          "Please enter your full name",
                        ]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setName(e.target.value)
                        }
                        InputProps={fieldIconAdornment(
                          <PersonOutline fontSize="small" color="primary" />,
                        )}
                      />
                    </motion.div>
                    <motion.div {...fieldMotion(0.55)}>
                      <TextValidator
                        fullWidth
                        name="phone"
                        label="Phone Number (optional)"
                        size="medium"
                        value={phoneNumber}
                        variant="outlined"
                        disabled={socialDisabled}
                        validators={["maxStringLength:15"]}
                        errorMessages={[
                          "Phone number must be less than 15 characters",
                        ]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setPhoneNumber(e.target.value)
                        }
                        InputProps={fieldIconAdornment(
                          <PhoneOutlined fontSize="small" color="primary" />,
                        )}
                      />
                    </motion.div>
                  </Stack>

                  <motion.div {...fieldMotion(0.55)}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="flex-start"
                      sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.06,
                        ),
                      })}
                    >
                      <MarkEmailReadOutlined
                        sx={{ fontSize: 18, color: "primary.main", mt: 0.25 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        lineHeight={1.5}
                      >
                        Email signups receive a verification link to confirm
                        your address and set your password. Social signups skip
                        this step.
                      </Typography>
                    </Stack>
                  </motion.div>

                  <motion.div {...fieldMotion(0.62)}>
                    <Button
                      type="submit"
                      fullWidth
                      size="large"
                      variant="contained"
                      color="primary"
                      disabled={
                        submitting ||
                        socialSubmitting !== null ||
                        !isEmailSignupReady
                      }
                      endIcon={<ArrowForward />}
                      sx={{ py: 1.4, fontWeight: 700 }}
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </>
              )}

              <motion.div {...fieldMotion(0.34)}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    or
                  </Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>
              </motion.div>

              <Button
                startIcon={<EmailOutlined />}
                variant="outlined"
                fullWidth
                size="small"
                color="secondary"
                onClick={() => setShowEmailSignup(!showEmailSignup)}
              >
                {!showEmailSignup
                  ? "Sign up with email instead"
                  : "Sign up with social instead"}
              </Button>
            </>
          )}

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Already have an account?{" "}
            <Link
              component={RouterLink}
              to="/login"
              underline="hover"
              fontWeight={600}
            >
              Log in
            </Link>
          </Typography>
        </Stack>
      </ValidatorForm>
    </>
  );
}

export default WooCommerceSignupForm;
